import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSessionToken, useRevokedKickOut } from '@/hooks';
import { App, Spin, Result, Button as AntButton } from 'antd';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HTS_PERFECT_PERSON_PROBE_ID,
  HtsInputType,
  HtsYesNoAnswer,
  HtsSubmissionStatus,
} from '@evaltick/shared';
import { HtsService, BaseAPIClient, isHtsPendingPerfectPersonProbe } from '@/services/api';
import type {
  HtsQuestion,
  HtsQuestionsForTemplate,
  HtsAnswer,
  HtsAnswerInput,
  HtsFollowUpExchange,
} from '@/services/api';
import { ROUTES, buildRoute } from '@/routes';
import { HtsTopbar } from './HtsTopbar';
import { HtsQuestionCard, parseCheckboxRowValue } from './HtsQuestionCard';
import { HtsWaiverModal } from './HtsWaiverModal';
import { HtsExitModal } from './HtsExitModal';
import { HtsSubmitModal } from './HtsSubmitModal';
import styles from './HtsQuestionnaire.module.scss';

type Answers = Record<string, string | string[]>;

export type DisplayQuestion = HtsQuestion;

/**
 * Splits a submission's saved answers back into the client-side stores:
 * every answer is keyed by its own question id, and any answer with a
 * still-pending AI follow-up question is surfaced so resuming a draft
 * mid-follow-up doesn't silently lose it.
 */
function hydrateSubmissionAnswers(submissionAnswers: HtsAnswer[]): {
  answers: Answers;
  followUpHistories: Record<string, HtsFollowUpExchange[]>;
  redoUsed: Record<string, boolean>;
} {
  const answers: Answers = {};
  const followUpHistories: Record<string, HtsFollowUpExchange[]> = {};
  const redoUsed: Record<string, boolean> = {};
  for (const answer of submissionAnswers) {
    answers[answer.questionId] = answer.value;
    if (answer.followUpHistory?.length) followUpHistories[answer.questionId] = answer.followUpHistory;
    if (answer.redoUsed) redoUsed[answer.questionId] = true;
  }
  return { answers, followUpHistories, redoUsed };
}

function hasValue(v: string | string[] | undefined): boolean {
  if (Array.isArray(v)) return v.length > 0;
  return !!v?.trim();
}

// Reference equality doesn't work for the array-valued (CHECKBOXES) case —
// two separately-built arrays with the same options selected are still
// different array instances. Used to tell "actually edited" apart from
// "revisited but unchanged," so flushCurrentAnswer doesn't re-save (and
// re-run the AI classifier on) a value that hasn't moved.
function valuesEqual(a: string | string[] | undefined, b: string | string[] | undefined): boolean {
  if (Array.isArray(a) || Array.isArray(b)) {
    const arrA = Array.isArray(a) ? a : [];
    const arrB = Array.isArray(b) ? b : [];
    return arrA.length === arrB.length && arrA.every((v, i) => v === arrB[i]);
  }
  return a === b;
}

// A row counts as complete only once every one of its sub-fields has a
// real value — an added-but-untouched row (e.g. `{}`) must not let the
// question pass as answered.
function isRowComplete(row: Record<string, string>, fields: HtsQuestion['groupFields']): boolean {
  return (fields ?? []).every((field) => {
    const raw = row[field.key];
    if (field.inputType === HtsInputType.CHECKBOXES) {
      return parseCheckboxRowValue(raw).length > 0;
    }
    return typeof raw === 'string' && raw.trim().length > 0;
  });
}

function checkIsAnswered(q: DisplayQuestion, answers: Answers): boolean {
  const v = answers[q.id];
  if (q.inputType === HtsInputType.YES_NO) {
    return v === HtsYesNoAnswer.YES || v === HtsYesNoAnswer.NO;
  }
  if (q.inputType === HtsInputType.REPEATABLE_GROUP) {
    // Answered means "explicitly touched," not "has at least one row" — a
    // candidate with nothing to report (e.g. no other agencies applied to)
    // marks the field "none," which is stored as the empty array "[]" and
    // must count as answered, distinct from never having touched the field
    // at all (undefined/empty string).
    if (typeof v !== 'string' || !v.trim()) return false;
    try {
      const rows = JSON.parse(v);
      if (!Array.isArray(rows)) return false;
      return rows.every((row) => isRowComplete(row, q.groupFields));
    } catch {
      return false;
    }
  }
  return hasValue(v);
}

export const HtsQuestionnaire: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getToken } = useSessionToken();
  const { notification } = App.useApp();
  const handleIfRevoked = useRevokedKickOut();
  const templateId = searchParams.get('templateId') ?? '';

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [questionnaire, setQuestionnaire] = useState<HtsQuestionsForTemplate | null>(null);
  const [submissionId, setSubmissionId] = useState<string | null>(null);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  // The full AI follow-up conversation per question id — every round
  // asked/answered so far, plus a still-pending one (if any) as the last
  // entry — and the candidate's in-progress answer to that pending entry.
  const [followUpHistories, setFollowUpHistories] = useState<Record<string, HtsFollowUpExchange[]>>({});
  const [followUpAnswerDrafts, setFollowUpAnswerDrafts] = useState<Record<string, string>>({});
  // The last value actually persisted to the server per question id — normal
  // questions are always editable (Back unlocks revisiting and changing an
  // earlier answer), so this replaces the old permanent "locked once saved"
  // set: it's what flushCurrentAnswer diffs the live answer against to know
  // whether there's anything new to save at all.
  const [lastSavedAnswers, setLastSavedAnswers] = useState<Answers>({});
  // Which questions have already used their one-time redo (see saveHtsAnswers) —
  // a further change to one of these is rejected by the server, so the card
  // renders it read-only.
  const [redoUsed, setRedoUsed] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  // The fixed "perfect person" question. `perfectPersonQuestion` is decided
  // *once*, the first time the candidate reaches the end of the
  // form: `checkPerfectPersonProbe` runs, and if it applies, this is set and
  // never cleared again — from then on it behaves like a normal, permanent
  // question, regardless of what the candidate later changes elsewhere. If
  // it never applies (still null after a check), the check simply runs again
  // next time they reach the end, so it can still appear later if an earlier
  // answer gets fixed up.
  const [perfectPersonQuestion, setPerfectPersonQuestion] = useState<string | null>(null);
  const [perfectPersonAnswer, setPerfectPersonAnswer] = useState('');
  // `answerPerfectPersonProbe` is a write-once save (the server 404s on a
  // repeat call) — tracks whether it's already gone through, so reopening the
  // confirm modal via "Review answers" and pressing submit again on this same
  // card doesn't try to save it a second time.
  const [perfectPersonSaved, setPerfectPersonSaved] = useState(false);
  // Whether the perfect-person card is the thing on screen right now, as
  // opposed to whether it exists at all (`perfectPersonQuestion`) — lets the
  // candidate step Back to the last normal question and back again.
  const [viewingPerfectPerson, setViewingPerfectPerson] = useState(false);
  // Decided after load: the waiver shows only when no submission exists yet
  // (a submission only comes into being once the waiver has been accepted).
  const [showWaiver, setShowWaiver] = useState(false);
  const [showExit, setShowExit] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    if (!templateId) {
      navigate(ROUTES.SESSIONS, { replace: true });
      return;
    }

    setLoading(true);
    setLoadError(null);

    let cancelled = false;
    const load = async () => {
      try {
        const token = await getToken();
        if (!token) throw new Error('Not authenticated');

        // A single read: questions plus the user's own submission (or null).
        // We deliberately don't create a submission here — that only happens
        // once the candidate accepts the consent waiver.
        const questionnaireResult = await new HtsService(token).getQuestionsForTemplate(templateId);
        if (cancelled) return;

        const { submission } = questionnaireResult;
        if (submission?.status === HtsSubmissionStatus.SUBMITTED) {
          navigate(ROUTES.SESSIONS, { replace: true });
          return;
        }

        setQuestionnaire(questionnaireResult);

        if (submission) {
          // Resuming an existing draft — consent was already recorded when it
          // was created, so the waiver stays hidden.
          const {
            answers: hydratedAnswers,
            followUpHistories: hydratedFollowUpHistories,
            redoUsed: hydratedRedoUsed,
          } = hydrateSubmissionAnswers(submission.answers);
          setSubmissionId(submission.id);
          setAnswers(hydratedAnswers);
          // Everything hydrated from the server is, by definition, already
          // saved — seeds the diff flushCurrentAnswer uses to tell an edit
          // apart from an unchanged revisit.
          setLastSavedAnswers(hydratedAnswers);
          setFollowUpHistories(hydratedFollowUpHistories);
          setRedoUsed(hydratedRedoUsed);

          // Resume at the first unanswered question overall, not always
          // question 1 — falls back to the last question if everything's
          // already answered (e.g. only the perfect-person probe is left).
          const firstUnanswered = questionnaireResult.questions.findIndex(
            (q) => !checkIsAnswered(q, hydratedAnswers)
          );
          setCurrentIndex(
            firstUnanswered === -1 ? Math.max(0, questionnaireResult.questions.length - 1) : firstUnanswered
          );

          // If the fixed question was already answered in an earlier visit,
          // restore it as a normal, permanent question (rail entry + modal
          // count) rather than losing that state; not forced on screen, same
          // as any other already-answered question.
          if (submission.perfectPersonProbe) {
            setPerfectPersonQuestion(submission.perfectPersonProbe.question);
            setPerfectPersonAnswer(submission.perfectPersonProbe.answer);
            setPerfectPersonSaved(true);
          }
        } else {
          // Brand new — require consent before a submission is created.
          setShowWaiver(true);
        }
      } catch (err) {
        if (!cancelled && !handleIfRevoked(err)) setLoadError(BaseAPIClient.parseErrorMsg(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [templateId, reloadNonce]); // eslint-disable-line react-hooks/exhaustive-deps

  // Intercept the browser Back button so it goes through the same exit
  // confirmation as the in-app Exit button, instead of silently leaving with
  // whatever's on screen unflushed. There's no router-level blocker to reach
  // for here — this app uses a plain BrowserRouter, not a data router, so
  // react-router's useBlocker isn't available — so this is the standard
  // history/popstate trick: keep one extra entry pushed so a Back press
  // never actually leaves the page, and reuse HtsExitModal rather than a
  // second confirm surface.
  useEffect(() => {
    if (!submissionId) return;
    window.history.pushState(null, '', window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
      setShowExit(true);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [submissionId]);

  // Every question in the template — one attempt per template, ever, so
  // there's no scoped subset left to filter to.
  const displayQuestions = questionnaire?.questions ?? [];

  const isAnswered = (q: DisplayQuestion) => checkIsAnswered(q, answers);
  const answeredCount = displayQuestions.filter(isAnswered).length;
  const allAnswered = answeredCount === displayQuestions.length && displayQuestions.length > 0;

  const currentQuestion = displayQuestions[currentIndex];

  const handleChange = (value: string | string[]) => {
    if (!currentQuestion) return;
    setAnswers((prev) => ({ ...prev, [currentQuestion.id]: value }));
  };

  const handleFollowUpAnswerChange = (value: string) => {
    if (!currentQuestion) return;
    setFollowUpAnswerDrafts((prev) => ({ ...prev, [currentQuestion.id]: value }));
  };

  // The still-unanswered follow-up question for a question id, if its
  // history's last entry hasn't been answered yet — at most one at a time.
  const pendingFollowUpFor = (questionId: string): string | undefined => {
    const history = followUpHistories[questionId];
    const last = history?.[history.length - 1];
    return last && last.answer === null ? last.question : undefined;
  };

  // Returns { blocked: true } when the candidate must not be allowed to move
  // past the current question yet — either an AI follow-up question is
  // pending and unanswered, or one was just raised by this very save.
  const flushCurrentAnswer = async (): Promise<{ blocked: boolean }> => {
    if (!submissionId || !currentQuestion) return { blocked: false };

    const pendingFollowUp = pendingFollowUpFor(currentQuestion.id);
    const followUpDraft = followUpAnswerDrafts[currentQuestion.id]?.trim();
    const value = answers[currentQuestion.id];
    const parentChanged = !valuesEqual(value, lastSavedAnswers[currentQuestion.id]);

    // Editing the parent value is itself a redo that invalidates any pending
    // follow-up thread (see saveHtsAnswers) — only require an answer to it
    // when the parent value itself hasn't moved.
    if (pendingFollowUp && !parentChanged && !followUpDraft) return { blocked: true };

    // Re-sending an unchanged value would re-run the classifier over it for
    // no reason — a model call per Back/Next navigation that can overwrite
    // the stored verdict or raise a spurious follow-up. A pending follow-up
    // is genuinely new, so it still goes through regardless of whether the
    // answer itself moved.
    if (!pendingFollowUp && !parentChanged) {
      return { blocked: false };
    }

    const inputs: HtsAnswerInput[] = [];
    if (value !== undefined) inputs.push({ questionId: currentQuestion.id, value });

    if (inputs.length === 0) {
      return { blocked: false };
    }

    setSaving(true);
    try {
      const result = await new HtsService((await getToken()) ?? '').saveAnswers(
        submissionId,
        inputs,
        pendingFollowUp && !parentChanged && followUpDraft
          ? { questionId: currentQuestion.id, answer: followUpDraft }
          : undefined
      );

      setLastSavedAnswers((prev) => ({ ...prev, [currentQuestion.id]: value }));
      const savedAnswer = result.answers.find((a) => a.questionId === currentQuestion.id);
      if (savedAnswer?.redoUsed) {
        setRedoUsed((prev) => ({ ...prev, [currentQuestion.id]: true }));
      }

      const outcome = result.followUps[currentQuestion.id];
      if (outcome) {
        // The full conversation so far (every round asked/answered, plus a
        // newly-raised pending one if any) — kept on screen rather than
        // replaced/cleared, so the candidate can see everything they've
        // already answered for this question.
        setFollowUpHistories((prev) => ({ ...prev, [currentQuestion.id]: outcome.history }));
        setFollowUpAnswerDrafts((prev) => ({ ...prev, [currentQuestion.id]: '' }));
        if (!outcome.resolved && outcome.followUpQuestion) return { blocked: true };
      } else if (parentChanged) {
        // No outcome came back (the classifier failed and the server swallowed
        // it), but the save itself committed — and a changed value always
        // clears the thread server-side. Drop it here too, or the card keeps
        // demanding an answer to a follow-up that no longer exists.
        setFollowUpHistories((prev) => ({ ...prev, [currentQuestion.id]: [] }));
        setFollowUpAnswerDrafts((prev) => ({ ...prev, [currentQuestion.id]: '' }));
      }
      return { blocked: false };
    } catch (err) {
      if (!handleIfRevoked(err)) {
        notification.error({ message: 'Failed to save your answer', description: BaseAPIClient.parseErrorMsg(err) });
      }
      return { blocked: true };
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    const { blocked } = await flushCurrentAnswer();
    if (!blocked && currentIndex < displayQuestions.length - 1) setCurrentIndex((i) => i + 1);
  };

  // Unlike Next/Submit, Back never saves — glancing at a previous question
  // shouldn't persist (or spend the one-time redo for) whatever's drafted here.
  const handleBack = () => {
    setCurrentIndex((i) => Math.max(0, i - 1));
  };

  // The perfect-person probe isn't part of `answers`/flushCurrentAnswer at
  // all (it saves through its own write-once endpoint), so stepping back
  // from it is just hiding the overlay, nothing to flush.
  const handleBackFromPerfectPerson = () => setViewingPerfectPerson(false);

  const handleOpenSubmit = async () => {
    const { blocked } = await flushCurrentAnswer();
    if (blocked || !submissionId) return;

    // Already known from an earlier check this session (pending or already
    // answered) — it's a permanent part of the flow now, so just show it
    // again rather than re-checking whether it still applies.
    if (perfectPersonQuestion) {
      setViewingPerfectPerson(true);
      return;
    }

    // First time reaching the end — checked read-only, before the
    // confirm-submit modal, so the fixed question (if it applies) shows up as
    // one more ordinary question at the end of the flow rather than
    // interrupting the modal. If it doesn't apply yet, nothing is recorded
    // here, so this check simply runs again the next time Submit is pressed.
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const status = await new HtsService(token).checkPerfectPersonProbe(submissionId);
      if (status.perfectPersonQuestion) {
        setPerfectPersonQuestion(status.perfectPersonQuestion);
        if (status.perfectPersonAnswer) {
          setPerfectPersonAnswer(status.perfectPersonAnswer);
          setPerfectPersonSaved(true);
        }
        setViewingPerfectPerson(true);
        return;
      }
    } catch (err) {
      if (!handleIfRevoked(err)) {
        notification.error({ message: 'Could not open submission', description: BaseAPIClient.parseErrorMsg(err) });
      }
      return;
    }
    setShowSubmit(true);
  };

  // Every question whose on-screen value hasn't reached the server. Exiting
  // saves nothing, so HtsExitModal names these to make the discard explicit.
  const unsavedQuestions = displayQuestions
    .map((question, index) => ({ question, index }))
    .filter(({ question }) => {
      const current = answers[question.id];
      const saved = lastSavedAnswers[question.id];
      if (!hasValue(current) && !hasValue(saved)) return false; // never had anything to lose
      return !valuesEqual(current, saved);
    })
    .map(({ question, index }) => ({ id: question.id, number: index + 1, text: question.text }));

  // Called from HtsExitModal's "Exit" — saves nothing, see `unsavedQuestions`.
  // `replace: true` so the dummy history entries pushed by the popstate guard
  // above don't leave a stale back-trail into the questionnaire once the
  // candidate has actually left it.
  const handleExit = () => {
    navigate(ROUTES.SESSIONS, { replace: true });
  };

  const handleSubmitConfirm = async () => {
    if (!submissionId) return;
    setSubmitting(true);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');

      // Write-once: only actually persisted here, at the real confirmation —
      // not on the probe card's own "Submit" click (see
      // handlePerfectPersonSubmit), so "Review answers" in between can still
      // freely edit it. Skipped if an earlier confirm attempt already saved
      // it (e.g. this is a retry after submit() itself failed).
      if (perfectPersonQuestion && perfectPersonAnswer.trim() && !perfectPersonSaved) {
        try {
          await new HtsService(token).answerPerfectPersonProbe(submissionId, perfectPersonAnswer.trim());
          setPerfectPersonSaved(true);
        } catch (err) {
          if (handleIfRevoked(err)) return;
          // Write-once, so a failure here is most likely a 404 from another
          // tab already answering it. Swallowed rather than surfaced —
          // submit() below re-checks probe-pending state and routes back to
          // the probe card if it's genuinely still unanswered.
          console.warn('Perfect-person probe save rejected — continuing to submit', err);
        }
      }

      const result = await new HtsService(token).submit(submissionId);
      // Shouldn't happen — handleOpenSubmit already checked this before the
      // modal was shown — but stay safe against a race (e.g. another tab).
      if (isHtsPendingPerfectPersonProbe(result)) {
        setPerfectPersonQuestion(result.perfectPersonQuestion);
        setViewingPerfectPerson(true);
        setShowSubmit(false);
        setSubmitting(false);
        return;
      }
      navigate(buildRoute(ROUTES.HTS_READINESS, { submissionId }), {
        replace: true,
        state: { htsJustSubmitted: true },
      });
    } catch (err) {
      if (handleIfRevoked(err)) return;
      notification.error({ message: 'Could not submit questionnaire', description: BaseAPIClient.parseErrorMsg(err) });
      setSubmitting(false);
      setShowSubmit(false);
    }
  };

  // Once the fixed question is answered, proceed to the same confirm-submit
  // modal any other completed questionnaire would show, rather than
  // finalizing immediately. Deliberately doesn't save anything — the probe
  // is write-once, so persisting it here would lock it before the candidate
  // has actually confirmed the submission (see handleSubmitConfirm, which is
  // where the real save happens).
  const handlePerfectPersonSubmit = () => {
    if (!perfectPersonAnswer.trim()) return;
    setShowSubmit(true);
  };

  // Accepting the waiver is what creates the submission — the server records
  // consent (consentedAt/consentVersion) at that moment, on the create branch.
  const handleWaiverAccept = async () => {
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const submission = await new HtsService(token).startOrResumeSubmission(templateId);
      const { answers: hydratedAnswers } = hydrateSubmissionAnswers(submission.answers);
      setAnswers(hydratedAnswers);
      setLastSavedAnswers(hydratedAnswers);
      setSubmissionId(submission.id);
      setShowWaiver(false);
    } catch (err) {
      if (handleIfRevoked(err)) return;
      notification.error({
        message: 'Could not start the questionnaire',
        description: BaseAPIClient.parseErrorMsg(err),
      });
    }
  };

  const handleWaiverDecline = () => {
    navigate(ROUTES.SESSIONS);
  };

  if (loading) {
    return (
      <div className={styles.shell}>
        <div className={styles.loadingWrap}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (loadError || !questionnaire || !currentQuestion) {
    return (
      <div className={styles.shell}>
        <div className={styles.loadingWrap}>
          <Result
            status="warning"
            title="This questionnaire couldn&rsquo;t be loaded"
            subTitle={loadError ?? 'This questionnaire is not available.'}
            extra={[
              <AntButton key="retry" type="primary" onClick={() => setReloadNonce((n) => n + 1)}>
                Try again
              </AntButton>,
              <AntButton key="back" onClick={() => navigate(ROUTES.SESSIONS, { replace: true })}>
                Back to Sessions
              </AntButton>,
            ]}
          />
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className={styles.shell}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <HtsTopbar
        currentQuestionNumber={currentIndex + 1}
        answeredCount={answeredCount}
        total={displayQuestions.length}
        onExit={() => setShowExit(true)}
      />

      <div className={styles.body}>
        <main className={styles.main}>
          <AnimatePresence mode="wait">
            <motion.div
              key={viewingPerfectPerson ? 'perfect-person' : currentIndex}
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className={styles.cardWrap}
            >
              {viewingPerfectPerson && perfectPersonQuestion ? (
                // Reuses the same card as any other question — it should look
                // identical, not flagged as special: read-only once saved,
                // since the server's write is write-once and a later edit
                // could never be persisted (unlike normal questions, which
                // are always editable now).
                <HtsQuestionCard
                  question={{
                    id: HTS_PERFECT_PERSON_PROBE_ID,
                    interviewTemplateId: currentQuestion.interviewTemplateId,
                    section: currentQuestion.section,
                    order: currentQuestion.order + 1,
                    inputType: HtsInputType.TEXTAREA,
                    text: perfectPersonQuestion,
                    helpText: null,
                    options: [],
                    groupFields: null,
                  }}
                  isLast
                  answer={perfectPersonAnswer}
                  allAnswered={!!perfectPersonAnswer.trim()}
                  currentAnswered={!!perfectPersonAnswer.trim()}
                  locked={perfectPersonSaved}
                  lockedCaption="Already submitted — this answer can't be changed."
                  showEditOnceNote={false}
                  onBack={handleBackFromPerfectPerson}
                  onChange={(value) => setPerfectPersonAnswer(typeof value === 'string' ? value : '')}
                  onNext={() => {}}
                  onSubmit={handlePerfectPersonSubmit}
                  saving={submitting}
                />
              ) : (
                <HtsQuestionCard
                  question={currentQuestion}
                  isLast={currentIndex === displayQuestions.length - 1}
                  answer={answers[currentQuestion.id]}
                  allAnswered={allAnswered}
                  currentAnswered={isAnswered(currentQuestion)}
                  hasSavedAnswer={lastSavedAnswers[currentQuestion.id] !== undefined}
                  hasUnsavedChanges={!valuesEqual(answers[currentQuestion.id], lastSavedAnswers[currentQuestion.id])}
                  lastSavedValue={lastSavedAnswers[currentQuestion.id]}
                  locked={!!redoUsed[currentQuestion.id]}
                  onBack={currentIndex > 0 ? handleBack : undefined}
                  onChange={handleChange}
                  onNext={handleNext}
                  onSubmit={handleOpenSubmit}
                  followUpHistory={followUpHistories[currentQuestion.id]}
                  followUpAnswer={followUpAnswerDrafts[currentQuestion.id]}
                  onFollowUpAnswerChange={handleFollowUpAnswerChange}
                  saving={saving}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {showWaiver && (
        <HtsWaiverModal
          isGuest={questionnaire.isGuest}
          onAccept={handleWaiverAccept}
          onDecline={handleWaiverDecline}
        />
      )}
      {showExit && (
        <HtsExitModal
          unsavedQuestions={unsavedQuestions}
          onStay={() => setShowExit(false)}
          onExit={handleExit}
        />
      )}
      {showSubmit && (
        <HtsSubmitModal
          total={displayQuestions.length + (perfectPersonQuestion ? 1 : 0)}
          onCancel={() => setShowSubmit(false)}
          onConfirm={handleSubmitConfirm}
          loading={submitting}
        />
      )}
    </motion.div>
  );
};
